import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card'
import { Button } from './ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog'
import { Label } from './ui/label'
import { RadioGroup, RadioGroupItem } from './ui/radio-group'
import { Download, Upload, FileJson, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'

const DataExportImport = ({ companyId, companyName }) => {
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [importFile, setImportFile] = useState(null)
  const [importMode, setImportMode] = useState('merge')
  const [importPreview, setImportPreview] = useState(null)
  const [importing, setImporting] = useState(false)
  const [exporting, setExporting] = useState(false)

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || ''

  const handleExport = async () => {
    setExporting(true)
    try {
      const response = await fetch(`${BACKEND_URL}/api/export-import/${companyId}/export`)
      
      if (response.ok) {
        const data = await response.json()
        
        // Create blob and download
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${companyName || 'company'}_backup_${new Date().toISOString().split('T')[0]}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
        
        toast.success('✅ Daten erfolgreich exportiert')
      } else {
        toast.error('❌ Export fehlgeschlagen')
      }
    } catch (error) {
      console.error('Export error:', error)
      toast.error('❌ Export fehlgeschlagen')
    } finally {
      setExporting(false)
    }
  }

  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      setImportFile(file)
      
      // Read and parse file for preview
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result)
          setImportPreview({
            services: data.services?.length || 0,
            packages: data.packages?.length || 0,
            categories: data.categories?.length || 0,
            customers: data.customers?.length || 0,
            quotes: data.quotes?.length || 0,
            exportDate: data.exportDate
          })
        } catch (error) {
          toast.error('❌ Ungültige JSON-Datei')
          setImportFile(null)
        }
      }
      reader.readAsText(file)
    }
  }

  const handleImport = async () => {
    if (!importFile || !importPreview) return
    
    setImporting(true)
    try {
      const reader = new FileReader()
      reader.onload = async (e) => {
        try {
          const data = JSON.parse(e.target.result)
          
          const response = await fetch(`${BACKEND_URL}/api/export-import/${companyId}/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              mode: importMode,
              data: data
            })
          })
          
          if (response.ok) {
            const result = await response.json()
            toast.success(`✅ Import erfolgreich: ${result.imported.services} Services, ${result.imported.categories} Kategorien, ${result.imported.packages} Pakete`)
            setImportDialogOpen(false)
            setImportFile(null)
            setImportPreview(null)
            
            // Reload page to show imported data
            setTimeout(() => window.location.reload(), 1500)
          } else {
            toast.error('❌ Import fehlgeschlagen')
          }
        } catch (error) {
          console.error('Import error:', error)
          toast.error('❌ Import fehlgeschlagen')
        } finally {
          setImporting(false)
        }
      }
      reader.readAsText(importFile)
    } catch (error) {
      console.error('Import error:', error)
      toast.error('❌ Import fehlgeschlagen')
      setImporting(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Export Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Daten exportieren
          </CardTitle>
          <CardDescription>
            Exportieren Sie alle Daten dieser Firma als JSON-Backup
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Exportiert werden: Services, Pakete, Kategorien, Kunden und Angebote
          </p>
          <Button 
            onClick={handleExport} 
            disabled={exporting}
            className="w-full sm:w-auto"
          >
            <FileJson className="w-4 h-4 mr-2" />
            {exporting ? 'Exportiere...' : 'Als JSON exportieren'}
          </Button>
        </CardContent>
      </Card>

      {/* Import Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Daten importieren
          </CardTitle>
          <CardDescription>
            Importieren Sie Daten aus einer JSON-Backup-Datei
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <p className="text-sm text-amber-800">
              Vorsicht: Import kann bestehende Daten überschreiben
            </p>
          </div>
          
          <Button 
            onClick={() => setImportDialogOpen(true)}
            variant="outline"
            className="w-full sm:w-auto"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import starten
          </Button>
        </CardContent>
      </Card>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Daten importieren</DialogTitle>
            <DialogDescription>
              Wählen Sie eine Backup-Datei und den Import-Modus
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* File Upload */}
            <div>
              <Label>JSON-Datei auswählen</Label>
              <input
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                className="mt-2 block w-full text-sm text-slate-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
            </div>

            {/* Preview */}
            {importPreview && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold mb-2">Vorschau:</h4>
                <ul className="text-sm space-y-1">
                  <li>📦 {importPreview.services} Services</li>
                  <li>📁 {importPreview.categories} Kategorien</li>
                  <li>🎁 {importPreview.packages} Pakete</li>
                  <li>👥 {importPreview.customers} Kunden</li>
                  <li>📄 {importPreview.quotes} Angebote</li>
                  <li className="text-slate-500 mt-2">
                    Export-Datum: {new Date(importPreview.exportDate).toLocaleString('de-DE')}
                  </li>
                </ul>
              </div>
            )}

            {/* Import Mode */}
            {importPreview && (
              <div>
                <Label>Import-Modus</Label>
                <RadioGroup value={importMode} onValueChange={setImportMode} className="mt-2">
                  <div className="flex items-center space-x-2 p-3 border rounded-lg">
                    <RadioGroupItem value="merge" id="merge" />
                    <Label htmlFor="merge" className="flex-1 cursor-pointer">
                      <div className="font-medium">Zusammenführen</div>
                      <div className="text-sm text-slate-500">
                        Bestehende Daten behalten, nur neue hinzufügen
                      </div>
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2 p-3 border rounded-lg border-red-200">
                    <RadioGroupItem value="replace" id="replace" />
                    <Label htmlFor="replace" className="flex-1 cursor-pointer">
                      <div className="font-medium text-red-700">Ersetzen</div>
                      <div className="text-sm text-red-600">
                        ⚠️ Alle bestehenden Daten löschen und ersetzen
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setImportDialogOpen(false)
                  setImportFile(null)
                  setImportPreview(null)
                }}
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleImport}
                disabled={!importFile || importing}
              >
                {importing ? 'Importiere...' : 'Jetzt importieren'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default DataExportImport
